import { useState, type ReactNode } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { downloadAccountDocument } from "@/lib/brokerAccounts";
import { bidHoldNote } from "@/lib/bondLifecycleTracker";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";
import { CollapsibleBox } from "@/components/CollapsibleBox";
import { Pill } from "@/components/bond-submission/SubmissionShell";
import {
  bondKindLabel,
  readBondRows,
  type BondRowView,
} from "@/lib/bondStateModel";
import {
  bondNumberCell,
  bondRowChip,
  isMutedBondRow,
} from "@/lib/bondRowDisplay";
import { formatBondCurrency, formatBondDate } from "@/lib/bondValueDisplay";

// BRK-S417 — the submission page's Bonds tab: one row per bond on THIS
// submission, read from the order view's `bonds[]` (PMS-S836, ahead of the
// pin) and nothing else. It is one record's own child rows, not a browsed
// list — no search, sort or paging — so it is a plain table (an EXCEPTION in
// test/lib/listview-conformance.test.ts, as the Owners panel is).
//
// Mobile-first: below md each row is a card that labels its own values; from
// md it is a table with a header row. The content column is about 720px at
// both 768 and 1024 (the nav takes the difference), so only the bond number,
// figures and dates keep to one line; the free text and a long chip wrap,
// which keeps Status in view. The cell padding is narrow for the same reason:
// eight columns' minimum widths sit close to 720px. The scroller is the last
// resort, so the page itself never scrolls sideways.
//
// D-10: money is formatted here for display only and dates read m/d/yyyy; a
// figure PMS did not publish reads "—", never $0 and never a guessed date.
//
// BRK-S441: an issued bond's number is a link that downloads its executed
// form (`documentId`, PMS-S836) through the ACCOUNT's document
// route; a row with no document id has no link. A failed download says so on
// its row. BRK-S502: a bid bond on its way reads PMS's hold ("On hold · GIA
// Form needed") as its status, as the current-bond card does.

const HEADERS = [
  "Bond no.",
  "Bond",
  "Form",
  "Contract price",
  "Penal sum",
  "Basis",
  "Effective",
  "Status",
] as const;

const CELL =
  "flex items-baseline justify-between gap-3 py-1 md:table-cell md:px-1 md:py-3 md:align-top";
/** The column name a card shows beside a value; the table has a header row. */
const CARD_LABEL = "shrink-0 text-xs text-brk-dim md:hidden";
const DASH = "—";

const money = (n: number | null) => (n === null ? DASH : formatBondCurrency(n));
const date = (iso: string | null) =>
  iso === null ? DASH : formatBondDate(iso);

export function SubmissionBondsPanel({
  bonds,
  accountId,
  lifecycle,
}: {
  bonds: unknown;
  /** The order's account: the forms download through its document route. */
  accountId: string;
  /** The order's `lifecycle`, for PMS's hold on the bid bond. */
  lifecycle?: unknown;
}) {
  const rows = readBondRows(bonds);
  const hold = bidHoldNote(lifecycle);
  if (rows.length === 0) {
    return (
      <CollapsibleBox title="Bonds">
        <p className="text-sm text-brk-dim">No bonds on this submission yet.</p>
      </CollapsibleBox>
    );
  }
  return (
    <CollapsibleBox title={`Bonds (${String(rows.length)})`}>
      <div className="md:overflow-x-auto">
        <table
          role="table"
          aria-label="Bonds on this submission"
          className="block w-full text-sm md:table"
        >
          <thead role="rowgroup" className="hidden md:table-header-group">
            <tr role="row" className="text-left text-xs text-brk-dim">
              {HEADERS.map((h) => (
                <th
                  key={h}
                  role="columnheader"
                  scope="col"
                  className="px-1 py-2 font-medium"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody
            role="rowgroup"
            className="flex flex-col gap-3 md:table-row-group"
          >
            {rows.map((row) => (
              <BondTableRow
                key={row.id}
                row={row}
                accountId={accountId}
                hold={hold}
              />
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleBox>
  );
}

function BondTableRow({
  row,
  accountId,
  hold,
}: {
  row: BondRowView;
  accountId: string;
  hold: string | null;
}) {
  const muted = isMutedBondRow(row);
  const number = bondNumberCell(row);
  return (
    // Explicit roles: a row restyled as a card keeps its table semantics in
    // every browser.
    <tr
      role="row"
      data-muted={muted ? "true" : undefined}
      className={`flex flex-col rounded-lg border border-brk-border p-3 md:table-row md:rounded-none md:border-0 md:border-t md:p-0 ${
        muted ? "text-brk-dim" : "text-brk-text"
      }`}
    >
      <Cell label="Bond no.">
        {row.documentId !== null && !number.placeholder ? (
          <BondFormLink
            number={number.text}
            accountId={accountId}
            documentId={row.documentId}
          />
        ) : (
          <span
            className={`whitespace-nowrap tabular-nums ${
              number.placeholder ? "text-brk-dim" : "font-medium"
            }`}
          >
            {number.text}
          </span>
        )}
      </Cell>
      <Cell label="Bond">
        <span className="flex flex-col items-end md:items-start">
          <span>{bondKindLabel(row.kind)}</span>
          {row.pendingRider !== null && (
            <span className="text-xs font-medium text-violet-700 dark:text-violet-300">
              Rider request pending
            </span>
          )}
        </span>
      </Cell>
      <Cell label="Form" wraps>
        {row.formTitle ?? DASH}
      </Cell>
      <Cell label="Contract price">
        <span className="whitespace-nowrap tabular-nums">
          {money(row.contractPrice)}
        </span>
      </Cell>
      <Cell label="Penal sum">
        <span className="whitespace-nowrap tabular-nums">
          {money(row.penalSum)}
        </span>
      </Cell>
      <Cell label="Basis" wraps>
        {row.basis ?? DASH}
      </Cell>
      <Cell label="Effective">
        <span className="whitespace-nowrap tabular-nums">
          {date(row.effectiveDate)}
        </span>
      </Cell>
      <Cell label="Status">
        <Pill pill={bondRowChip(row, hold)} />
      </Cell>
    </tr>
  );
}

function Cell({
  label,
  wraps = false,
  children,
}: {
  label: string;
  /** Free text that may run long: it wraps rather than widening the card. */
  wraps?: boolean;
  children: ReactNode;
}) {
  return (
    <td role="cell" className={CELL}>
      <span className={CARD_LABEL}>{`${label} `}</span>
      {wraps ? (
        <span className="min-w-0 break-words text-right md:text-left">
          {children}
        </span>
      ) : (
        children
      )}
    </td>
  );
}

/** The bond number as a download of its executed form (BRK-S441). The SERVER
 *  names the file (`saveFile`); a failure stays on the row, in words. */
function BondFormLink({
  number,
  accountId,
  documentId,
}: {
  number: string;
  accountId: string;
  documentId: string;
}) {
  const { authedBlob } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function download() {
    setBusy(true);
    setError(null);
    try {
      saveFile(
        await downloadAccountDocument(authedBlob, accountId, documentId),
        `${number}.pdf`,
      );
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="inline-flex max-w-full flex-col items-end gap-1 md:items-start">
      <button
        type="button"
        onClick={() => void download()}
        disabled={busy}
        aria-label={`Download the ${number} bond form`}
        className="whitespace-nowrap font-medium tabular-nums text-brk-accent hover:underline disabled:opacity-60"
      >
        {busy ? "Preparing…" : number}
      </button>
      {error && (
        <span role="alert" className="text-xs text-brk-danger">
          {error}
        </span>
      )}
    </span>
  );
}
